import { useEffect, useState } from "react";
import { Sparkles, Loader2, Check, X, ExternalLink, Undo2 } from "lucide-react";
import { toast } from "sonner";
import { api, errText, shortDate } from "./api";
import { SITE } from "../data/site";

const Panel = ({ title, children, testid, right }) => (
  <section className="rounded-2xl bg-white p-5" data-testid={testid}>
    <div className="flex flex-wrap items-center justify-between gap-3"><h2 className="font-display text-2xl font-black uppercase">{title}</h2>{right}</div>
    {children}
  </section>
);

const Rows = ({ rows, label }) => (
  <div className="mt-3 overflow-x-auto">
    <table className="w-full min-w-[480px] text-sm">
      <thead><tr className="text-left text-xs uppercase tracking-widest text-slate-500"><th className="py-2">{label}</th><th>Clicks</th><th>Views</th><th>Click rate</th><th>Avg. spot</th></tr></thead>
      <tbody>{rows.slice(0, 15).map((r) => (
        <tr key={r.key} className="border-t border-slate-100"><td className="max-w-[260px] truncate py-2 pr-3 font-medium">{r.key.replace(SITE, "") || "/"}</td>
          <td>{r.clicks}</td><td>{r.impressions}</td><td>{(r.ctr * 100).toFixed(1)}%</td><td>{r.position.toFixed(1)}</td></tr>
      ))}</tbody>
    </table>
  </div>
);

const SearchNumbers = () => {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/admin/seo/search?days=28").then((r) => setD(r.data)).catch(() => setD({ connected: false })); }, []);
  if (!d) return <Panel title="Google search numbers" testid="seo-search-panel"><Loader2 className="mt-3 h-5 w-5 animate-spin text-slate-400" /></Panel>;
  if (!d.connected) return (
    <Panel title="Google search numbers" testid="seo-search-panel">
      <p className="mt-3 text-sm text-slate-600" data-testid="seo-search-not-connected">{d.error || "Not connected yet."} Once your Google Search Console is connected you'll see which searches show your site, how many people click, and your average spot on Google, for every page. The daily AI ideas will use these real search terms too.</p>
    </Panel>
  );
  return (
    <Panel title="Google search numbers" testid="seo-search-panel" right={<span className="text-xs text-slate-500">{d.start} to {d.end}</span>}>
      <div className="mt-3 grid grid-cols-2 gap-3">
        <div className="rounded-xl bg-slate-50 p-4"><p className="text-xs font-bold uppercase tracking-widest text-slate-500">Clicks</p><p className="font-display text-3xl font-black" data-testid="seo-total-clicks">{d.totals?.clicks ?? 0}</p></div>
        <div className="rounded-xl bg-slate-50 p-4"><p className="text-xs font-bold uppercase tracking-widest text-slate-500">Times shown</p><p className="font-display text-3xl font-black" data-testid="seo-total-impressions">{d.totals?.impressions ?? 0}</p></div>
      </div>
      <h3 className="mt-5 font-bold">Top searches</h3><Rows rows={d.queries ?? []} label="Search" />
      <h3 className="mt-5 font-bold">Top pages</h3><Rows rows={d.pages ?? []} label="Page" />
    </Panel>
  );
};

const Idea = ({ idea, onDone }) => {
  const [title, setTitle] = useState(idea.title);
  const [desc, setDesc] = useState(idea.description);
  const [busy, setBusy] = useState("");
  const act = async (kind) => {
    setBusy(kind);
    try {
      if (kind === "apply") await api.post(`/admin/seo/ideas/${idea.id}/apply`, { title, description: desc });
      else await api.post(`/admin/seo/ideas/${idea.id}/dismiss`);
      toast.success(kind === "apply" ? "Live on the site now" : "Dismissed"); onDone();
    } catch (e) { toast.error(errText(e)); } finally { setBusy(""); }
  };
  return (
    <li className="rounded-xl border border-slate-200 p-4" data-testid={`seo-idea-${idea.id}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <a href={idea.path} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-sm font-bold underline underline-offset-2">{idea.path} <ExternalLink className="h-3.5 w-3.5" /></a>
        <span className="rounded-full bg-slate-900 px-3 py-1 text-xs font-bold text-white">{idea.keyword}</span>
      </div>
      <p className="mt-2 text-sm text-slate-600">{idea.reason}</p>
      <label className="mt-3 block text-xs font-bold uppercase tracking-widest text-slate-500">Google title <span className={title.length > 60 ? "text-red-600" : ""}>({title.length}/60)</span>
        <input className="inp mt-1" value={title} onChange={(e) => setTitle(e.target.value)} data-testid="seo-idea-title-input" /></label>
      <label className="mt-3 block text-xs font-bold uppercase tracking-widest text-slate-500">Google description <span className={desc.length > 155 ? "text-red-600" : ""}>({desc.length}/155)</span>
        <textarea rows={3} className="inp mt-1" value={desc} onChange={(e) => setDesc(e.target.value)} data-testid="seo-idea-description-input" /></label>
      <div className="mt-3 flex gap-2">
        <button onClick={() => act("apply")} disabled={!!busy} className="flex items-center gap-2 rounded-full bg-slate-900 px-4 py-2.5 text-sm font-bold text-white disabled:opacity-60" data-testid="seo-idea-apply-button">{busy === "apply" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}Use on site</button>
        <button onClick={() => act("dismiss")} disabled={!!busy} className="flex items-center gap-2 rounded-full border border-slate-300 px-4 py-2.5 text-sm font-semibold disabled:opacity-60" data-testid="seo-idea-dismiss-button"><X className="h-4 w-4" />Dismiss</button>
      </div>
    </li>
  );
};

const Ideas = ({ onApplied }) => {
  const [d, setD] = useState(null);
  const [running, setRunning] = useState(false);
  const load = () => api.get("/admin/seo/ideas").then((r) => { setD(r.data); setRunning(r.data?.last_run?.status === "running"); }).catch(() => setD({ ideas: [] }));
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (!running) return undefined;
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [running]);
  const run = async () => {
    try { await api.post("/admin/seo/ideas/run"); setRunning(true); toast("Writing new ideas, about a minute…"); }
    catch (e) { toast.error(errText(e)); }
  };
  const last = d?.last_run;
  return (
    <Panel title="Daily AI keyword ideas" testid="seo-ideas-panel" right={
      <button onClick={run} disabled={running} className="flex items-center gap-2 rounded-full bg-slate-900 px-4 py-2.5 text-sm font-bold text-white disabled:opacity-60" data-testid="seo-run-ideas-button">
        {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}{running ? "Writing ideas…" : "Get new ideas now"}</button>}>
      <p className="mt-2 text-sm text-slate-600">Every morning at 7am, AI picks 6 pages and suggests the search phrase homeowners type plus a better Google title and description. Edit if you like, then tap <b>Use on site</b>, and it's live right away.</p>
      {last && <p className="mt-1 text-xs text-slate-500" data-testid="seo-last-run">Last run: {shortDate(last.started_at)} · {last.status === "failed" ? "failed, try again" : last.status === "running" ? "in progress" : `${last.count ?? 0} ideas`}</p>}
      {!d ? <Loader2 className="mt-3 h-5 w-5 animate-spin text-slate-400" /> : (
        <ul className="mt-4 grid gap-3 lg:grid-cols-2">
          {(d.ideas ?? []).length === 0 && <li className="text-sm text-slate-500" data-testid="seo-ideas-empty">No new ideas waiting. Tap "Get new ideas now" or check back tomorrow morning.</li>}
          {(d.ideas ?? []).map((i) => <Idea key={i.id} idea={i} onDone={() => { load(); onApplied(); }} />)}
        </ul>
      )}
    </Panel>
  );
};

const Applied = ({ version }) => {
  const [rows, setRows] = useState([]);
  const load = () => api.get("/admin/seo/overrides").then((r) => setRows(Array.isArray(r.data) ? r.data : [])).catch(() => setRows([]));
  useEffect(() => { load(); }, [version]);
  const undo = async (path) => { await api.delete(`/admin/seo/overrides?path=${encodeURIComponent(path)}`); toast.success("Back to the original wording"); load(); };
  return (
    <Panel title="Live on the site" testid="seo-live-section">
      {rows.length === 0 ? <p className="mt-3 text-sm text-slate-500">Nothing changed yet. Pages use their built-in titles.</p> : (
        <ul className="mt-3 divide-y divide-slate-100">
          {rows.map((r) => (
            <li key={r.path} className="flex flex-wrap items-start justify-between gap-3 py-3" data-testid={`seo-applied-${r.path}`}>
              <div className="min-w-0"><p className="text-sm font-bold">{r.path}</p><p className="text-sm">{r.title}</p><p className="text-xs text-slate-500">{r.description}</p>
                <p className="mt-1 text-xs text-slate-400">by {r.applied_by} · {shortDate(r.applied_at)}</p></div>
              <button onClick={() => undo(r.path)} className="flex items-center gap-1 rounded-full border border-slate-300 px-3 py-2 text-xs font-semibold" data-testid="seo-undo-button"><Undo2 className="h-3.5 w-3.5" />Undo</button>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
};

export default function SeoPanel() {
  const [version, setVersion] = useState(0);
  return (
    <div className="grid gap-4" data-testid="admin-seo">
      <h1 className="font-display text-4xl font-black uppercase">SEO</h1>
      <Ideas onApplied={() => setVersion((v) => v + 1)} />
      <SearchNumbers />
      <Applied version={version} />
    </div>
  );
}
